import { createSlice, isAnyOf } from '@reduxjs/toolkit';
import {
  getPlaygroundAssistants,
  resetSession,
  uploadFile,
  replyAssistantPlayground,
  replyAiChatPlayground,
  initAssistantPlayground,
  initChatPlayground,
  initMediaPlayground,
} from './aiPlaygroundActions';
import { PLAYGROUND_RESPONSE_TYPES } from '../../constants';

const aiPlaygroundInitialState = {
  assistantsData: [],
  aiChats: [],
  mediaTypes: [],
  selectedAssistant: null,

  threadId: '',
  responses: [],

  isFormLoading: false,
  isGenerating: false,
  isResetting: false,
  isReplying: false,
  isFileUploading: false,
};

const aiPlaygroundSlice = createSlice({
  name: 'aiPlayground',
  initialState: aiPlaygroundInitialState,
  reducers: {
    setSelectedAssistant: (state, action) => {
      state.selectedAssistant = action.payload;
    },
    resetAIPlayground: () => aiPlaygroundInitialState,
  },
  extraReducers: (builder) => {
    builder
      //GET ASSISTANTS
      .addCase(getPlaygroundAssistants.fulfilled, (state, action) => {
        state.isFormLoading = false;
        state.assistantsData = action.payload.assistants;
        state.aiChats = action.payload.aiChats;
        state.mediaTypes = action.payload.mediaTypes;
      })
      .addCase(getPlaygroundAssistants.rejected, (state) => {
        state.isFormLoading = false;
      })
      .addCase(getPlaygroundAssistants.pending, (state) => {
        state.isFormLoading = true;
      })

      // RESET SESSION
      .addCase(resetSession.fulfilled, (state) => {
        state.threadId = '';
        state.responses = [];
        state.isResetting = false;
      })
      .addCase(resetSession.rejected, (state) => {
        state.threadId = '';
        state.responses = [];
        state.isResetting = false;
      })
      .addCase(resetSession.pending, (state) => {
        state.isResetting = true;
      })

      // UPLOAD FILE
      .addCase(uploadFile.fulfilled, (state) => {
        state.isFileUploading = false;
      })
      .addCase(uploadFile.rejected, (state) => {
        state.isFileUploading = false;
      })
      .addCase(uploadFile.pending, (state) => {
        state.isFileUploading = true;
      })

      // REPLY
      .addCase(replyAssistantPlayground.fulfilled, (state, action) => {
        state.isGenerating = false;

        const assistantMessage = { responseType: PLAYGROUND_RESPONSE_TYPES.ASSISTANT, message: action.payload };
        state.responses = [assistantMessage, ...state.responses];
      })
      .addCase(replyAiChatPlayground.fulfilled, (state, action) => {
        state.isGenerating = false;

        const assistantMessage = {
          responseType: PLAYGROUND_RESPONSE_TYPES.ASSISTANT,
          message: action.payload.response,
        };

        state.threadId = action.payload.threadId;
        state.responses = [assistantMessage, ...state.responses];
      })

      // INIT PLAYGROUND
      .addMatcher(isAnyOf(initMediaPlayground.fulfilled), (state, action) => {
        state.isGenerating = false;
        state.isFormLoading = false;

        const response = {
          responseType: PLAYGROUND_RESPONSE_TYPES.ASSISTANT,
          isMedia: true,
          mediaId: action.payload.mediaId,
          mediaType: action.payload.mediaType,
        };
        state.threadId = action.payload.mediaId;
        state.responses = [response, ...state.responses];
      })
      .addMatcher(isAnyOf(initAssistantPlayground.fulfilled, initChatPlayground.fulfilled), (state, action) => {
        state.isFormLoading = false;
        state.isGenerating = false;
        state.threadId = action.payload.threadId;

        const response = { responseType: PLAYGROUND_RESPONSE_TYPES.ASSISTANT, message: action.payload.response };
        state.responses = [response, ...state.responses];
      })
      .addMatcher(
        isAnyOf(initAssistantPlayground.rejected, initChatPlayground.rejected, initMediaPlayground.rejected),
        (state) => {
          state.isFormLoading = false;
          state.isGenerating = false;
        },
      )
      .addMatcher(
        isAnyOf(initAssistantPlayground.pending, initChatPlayground.pending, initMediaPlayground.pending),
        (state, action) => {
          const userMessage = { responseType: PLAYGROUND_RESPONSE_TYPES.USER, message: action.meta.arg.message };
          state.isFormLoading = true;
          state.isGenerating = true;

          state.responses = [userMessage, ...state.responses];
        },
      )

      .addMatcher(isAnyOf(replyAssistantPlayground.rejected, replyAiChatPlayground.rejected), (state) => {
        state.isGenerating = false;
      })
      .addMatcher(isAnyOf(replyAssistantPlayground.pending, replyAiChatPlayground.pending), (state, action) => {
        state.isGenerating = true;

        const userMessage = { responseType: PLAYGROUND_RESPONSE_TYPES.USER, message: action.meta.arg.reply };
        state.responses = [userMessage, ...state.responses];
      });
  },
});

export const { resetAIPlayground, setSelectedAssistant } = aiPlaygroundSlice.actions;

export default aiPlaygroundSlice.reducer;
